import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getObjectColors } from '@/utils/objectColors';
import { fmtHours, fmtRange, objectColorKey } from '../assistantFormat';
import type { AssistantObject, AssistantTask, SuggestionFit } from '../assistantTypes';
import { DueTag } from './DueTag';
import { ObjectTag } from './ObjectTag';
import { ReasonChips } from './ReasonChips';
import { ScorePill } from './ScorePill';

interface Props {
  fit: SuggestionFit;
  task: AssistantTask;
  rank: number;
  objects: Map<string, AssistantObject>;
  active: boolean;
  disabled?: boolean;
  onHover: (taskId: string | null) => void;
  onAssign: (fit: SuggestionFit) => void;
  /** The employee's `startPoint.source` — distance chips name the start point accordingly. */
  startSource?: string | null;
}

/** One suggestion in the employee mode: task, score, reasons and the assign button. */
export function SuggestionCard({ fit, task, rank, objects, active, disabled, onHover, onAssign, startSource }: Props) {
  const { t, i18n } = useTranslation('planning');
  const { color } = getObjectColors(objectColorKey(task.objectId, task.objectName));
  return (
    <div
      onMouseEnter={() => onHover(task.taskId ?? null)}
      onMouseLeave={() => onHover(null)}
      className={`rounded-lg border p-3 flex gap-3 ${active ? 'border-slate-300 shadow-sm' : 'border-slate-200'}`}
    >
      <span
        className="w-5 h-5 rounded-full text-[10px] font-bold text-white grid place-items-center shrink-0 mt-0.5 tabular-nums"
        style={{ background: color }}
      >
        {rank}
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex items-start gap-2">
          <span className="text-[13px] font-medium text-slate-900 flex-1 min-w-0">{task.name}</span>
          <ScorePill score={fit.score} />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <ObjectTag objectId={task.objectId} objectName={task.objectName} label={task.objectShortName} />
          <span className="text-[11px] text-slate-500 tabular-nums">{fmtHours(task.durationMinutes, i18n.language)}</span>
          <DueTag due={task.dueCategory} />
        </div>
        <ReasonChips reasons={fit.reasons} objects={objects} startSource={startSource} />
        <div className="flex items-center gap-2 pt-1 flex-wrap">
          <button
            type="button"
            onClick={() => onAssign(fit)}
            disabled={disabled}
            className="whitespace-nowrap inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#00A871] text-white text-[12px] font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={13} strokeWidth={2} />
            {t('assistant.assign')}
            <span className="tabular-nums opacity-90">{fmtRange(fit.start, fit.end)}</span>
          </button>
          {(fit.travelInMinutes ?? 0) > 0 && (
            <span className="text-[11px] text-slate-400 tabular-nums">
              {t('assistant.travel', { minutes: fit.travelInMinutes })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
